// Tactile / haptic feedback primitives.
// Wraps navigator.vibrate so the rest of the codebase can call:
//   const haptic = useHaptic();
//   haptic.tap();        // light click ~ 10ms
//   haptic.tick();       // for sliders / step changes ~ 4ms
//   haptic.success();    // double pulse
//   haptic.warn();       // triple short pulse
// On unsupported devices (desktops, iOS Safari pre-16.4) this is a no-op.
import { useCallback, useEffect, useRef } from "react";

const supportsVibrate = () =>
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof navigator.vibrate === "function";

const PATTERNS = {
  tick: 5,
  tap: 10,
  success: [10, 30, 10],
  warn: [15, 40, 15, 40, 15],
  heavy: 25,
};

export function useHaptic() {
  // Throttle slider ticks so dragging doesn't spam vibrate (Android requires
  // user-gesture; we leave that to the calling event).
  const lastTickRef = useRef(0);

  const fire = useCallback((pattern) => {
    if (!supportsVibrate()) return;
    try { navigator.vibrate(pattern); } catch (_e) { /* swallow */ }
  }, []);

  const tap = useCallback(() => fire(PATTERNS.tap), [fire]);
  const tick = useCallback(() => {
    const now = Date.now();
    if (now - lastTickRef.current < 18) return;
    lastTickRef.current = now;
    fire(PATTERNS.tick);
  }, [fire]);
  const success = useCallback(() => fire(PATTERNS.success), [fire]);
  const warn = useCallback(() => fire(PATTERNS.warn), [fire]);
  const heavy = useCallback(() => fire(PATTERNS.heavy), [fire]);
  const custom = useCallback((p) => fire(p), [fire]);

  return { tap, tick, success, warn, heavy, custom, supported: supportsVibrate() };
}

/** Imperative singleton fallback for non-React modules (vanilla three.js etc). */
export const haptic = {
  tap: () => supportsVibrate() && navigator.vibrate(PATTERNS.tap),
  tick: (() => {
    let last = 0;
    return () => {
      if (!supportsVibrate()) return;
      const n = Date.now();
      if (n - last < 22) return;
      last = n;
      navigator.vibrate(PATTERNS.tick);
    };
  })(),
  success: () => supportsVibrate() && navigator.vibrate(PATTERNS.success),
  warn: () => supportsVibrate() && navigator.vibrate(PATTERNS.warn),
  heavy: () => supportsVibrate() && navigator.vibrate(PATTERNS.heavy),
};

/** Global click haptic — attaches once on body. Used by App.js so every
 *  interactive [data-haptic] element gets a tap on pointer down.
 *  Targets: any <button>, <a>, [role=button], [data-haptic], and inputs
 *  type=range (handled at change-event level by component instead). */
export function useGlobalClickHaptic() {
  useEffect(() => {
    if (!supportsVibrate()) return undefined;
    const handler = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      // Skip range inputs — slider components fire their own per-tick haptics
      const interactive = t.closest(
        'button, a[href], [role="button"], [data-haptic], input[type="checkbox"], input[type="radio"]'
      );
      if (!interactive) return;
      // Skip elements that opt out
      if (interactive.dataset?.haptic === "off") return;
      try { navigator.vibrate(PATTERNS.tap); } catch (_e) { /* swallow */ }
    };
    document.addEventListener("pointerdown", handler, { passive: true });

    // Range-input ticks — fire light haptic on each value change.
    let lastSliderTick = 0;
    const sliderHandler = (e) => {
      if (e.target?.type !== "range") return;
      if (e.target.dataset?.haptic === "off") return;
      const n = Date.now();
      if (n - lastSliderTick < 22) return;
      lastSliderTick = n;
      try { navigator.vibrate(PATTERNS.tick); } catch (_e) { /* swallow */ }
    };
    document.addEventListener("input", sliderHandler, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", handler);
      document.removeEventListener("input", sliderHandler);
    };
  }, []);
}
